import React, { useEffect, useState } from 'react';
import { siteToday } from '../../lib/when';
import { supabase } from '../../lib/supabase';
import Dialog from '../../components/Dialog';
import { IconPlus } from '../../components/dialogIcons';

/*
  Opening a count cycle.

  Built 5 September 2026 to Group E's approved drawing `dialog-open-cycle-*.png`,
  which is one of the two things that were not dialogs at all.

  **It was a form sitting on the empty Inventory Counts card**, four boxes and a
  button in a row, with nothing anywhere saying what pressing it does. What it
  does is make a count sheet for every active location in the company, 274 of
  them today, each carrying last cycle's items with every quantity blank. That
  is the largest single act in OnHand and it happened with less ceremony than
  removing one line from one sheet.

  **The sentence counts the real locations rather than describing the idea**, and
  it counts them by the same rule the database uses: a location that is inactive
  or marked do not use gets no sheet. Read out of `open_count_cycle` rather than
  guessed at, and checked against the database: 320 locations, 274 sheets, 46
  skipped.
*/
export default function OpenCycleDialog({ form, onChange, onOpen, onClose, busy }) {
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      // The same two flags the database checks, so the sentence and the act
      // cannot disagree.
      const all = await supabase.from('warehouses').select('id', { count: 'exact', head: true });
      const skip = await supabase.from('warehouses').select('id', { count: 'exact', head: true })
        .or('is_inactive.eq.true,is_do_not_use.eq.true');
      if (!live) return;
      const total = all.count || 0;
      const skipped = skip.count || 0;
      setCounts({ sheets: Math.max(0, total - skipped), skipped });
    })();
    return () => { live = false; };
  }, []);

  const ready = form.name.trim() && form.year;

  return (
    <Dialog
      kind="approve" icon={<IconPlus />}
      title="Open a count cycle"
      says="Only one cycle can be open at a time."
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-quiet" onClick={onClose}>Cancel</button>
          <button className="btn btn-approve" onClick={onOpen} disabled={!ready || busy}>
            {busy ? 'Opening...' : 'Open the cycle'}
          </button>
        </>
      }
    >
      <div className="form-lbl" style={{ marginTop: 0 }}>What to call it</div>
      <input className="form-inp" value={form.name} autoFocus
        onChange={e => onChange('name', e.target.value)} placeholder="e.g. Q2 2027 Count" />

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 160px', minWidth: 0 }}>
          <div className="form-lbl">Quarter</div>
          <select className="form-sel" value={form.quarter} onChange={e => onChange('quarter', e.target.value)}>
            <option>Q1</option><option>Q2</option><option>Q3</option><option>Q4</option>
          </select>
        </div>
        <div style={{ flex: '1 1 160px', minWidth: 0 }}>
          <div className="form-lbl">Year</div>
          <input className="form-inp" type="number" value={form.year}
            onChange={e => onChange('year', parseInt(e.target.value, 10))} />
        </div>
      </div>

      {/* **A count can have a day it should be finished by**, the owner's
          decision of 5 September 2026, which is after this group was drawn. It
          is optional: a count with no date behaves as every count did before. */}
      <div className="form-lbl">Finish by (optional)</div>
      {/* The earliest day allowed is TODAY WHERE THE SITE IS. It was today in
          Greenwich, so on a Texas evening the calendar refused today itself
          (the audit after the rebrand, issue 23, 18 September 2026). */}
      <input className="form-inp" type="date" min={siteToday()}
        value={form.dueOn} onChange={e => onChange('dueOn', e.target.value)} />

      <div style={{ marginTop: 16, borderRadius: 12, padding: '13px 15px', fontSize: 13, lineHeight: 1.55, background: 'var(--success-bg)', color: 'var(--success-ink)' }}>
        {counts === null
          ? 'Counting the locations...'
          : counts.sheets + ' count sheet' + (counts.sheets === 1 ? '' : 's') + ' will be made, one for each active location, '
            + "each with last cycle's items on it and every quantity blank."
            + (counts.skipped
                ? ' The ' + counts.skipped + ' inactive or do-not-use location'
                  + (counts.skipped === 1 ? '' : 's') + ' get none.'
                : '')}
      </div>
    </Dialog>
  );
}
